import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import {
  Button,
  ButtonLoader,
  emailText,
  FormLabel,
  Notification,
  requiredField,
  TextInput,
} from '@offerzen/pinata'
import { restClient } from 'config/restClient'
import ControlledHookField from '../../ControlledHookField'

import styles from './styles'

const validators = {
  required: requiredField('Enter one valid email address'),
  emailText: emailText('Enter one valid email address'),
}

const createSignTermsRequest = async (companyId, token, email) => {
  const response = await restClient(
    `/api/company/v2/company_profiles/${companyId}/sign_terms_request`,
    {
      method: 'POST',
      data: {
        token,
        email,
      },
    },
  )

  return response.data
}

const SendTermsForm = ({ companyId, token, setIsLoading }) => {
  const [success, setSuccess] = useState()
  const { control, handleSubmit, setError, formState, clearErrors, reset } = useForm({
    mode: 'onTouched',
  })
  const { formError } = formState.errors

  const { mutate: sendTerms, isLoading } = useMutation(
    values => createSignTermsRequest(companyId, token, values.email),
    {
      onSuccess: (_data, variables) => {
        setSuccess(`We’ve sent the terms to ${variables.email}.`)
        reset()
      },
      onError: () => {
        setError('formError', { message: 'Something went wrong and we couldn’t send the terms. Please try again.' })
      },
    },
  )

  useEffect(() => {
    setIsLoading(isLoading)
  }, [isLoading, setIsLoading])

  return (
    <form
      onSubmit={handleSubmit(values => sendTerms(values))}
      onChange={() => formError && clearErrors()}
      className={styles.form}
    >
      <FormLabel
        label="Email address"
        htmlFor="email"
        className={styles.label}
      />
      <ControlledHookField
        control={control}
        id="email"
        name="email"
        placeholder="Enter an email address"
        component={TextInput}
        className={styles.input}
        validate={[validators.required, validators.emailText]}
      />
      <Choose>
        <When condition={isLoading}>
          <ButtonLoader
            label="Sending"
            size="medium"
            className={styles.submitButton}
          />
        </When>
        <Otherwise>
          <Button
            label="Send terms"
            type="submit"
            size="medium"
            className={styles.submitButton}
          />
        </Otherwise>
      </Choose>
      <If condition={formError}>
        <Notification
          theme="error"
          role="alert"
          className={styles.notification}
        >
          {formError.message}
        </Notification>
      </If>
      <If condition={success}>
        <Notification
          theme="success"
          role="status"
          className={styles.notification}
        >
          {success}
        </Notification>
      </If>
    </form>
  )
}

SendTermsForm.propTypes = {
  companyId: PropTypes.string.isRequired,
  token: PropTypes.string,
  setIsLoading: PropTypes.func,
}

export default SendTermsForm
